import { Component, ErrorHandler, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { Message } from 'src/app/components/shared/message';
import { SituacaoFuncionalEnum } from 'src/app/components/shared/enums/situacao-funcional.enum';
import { SituacaoParcelaFeriasEnum } from 'src/app/components/shared/enums/situacao-parcela-ferias.enum';
import { PeriodoAquisitivo } from 'src/app/components/shared/models/periodo-aquisitivo';
import { getDateFromYYYYMMDD, getValidDateDDMMYYYY, isValidDate } from 'src/app/components/shared/date-utils';
import { formatarMensagem, generateUID } from 'src/app/components/shared/string-utils';
import { differenceInDays } from 'date-fns';
import { ExercicioSelecionado, ExerciciosService, FeriasEspeciaisAdministrativasComParcelas, ParcelaFeriasEspecialAdministrativaComParcelas, ParcelaFerias } from 'src/app/services/exercicios.service';
import { DataFormatadaPipe } from 'src/app/components/shared/pipes/dataFormatada.pipe';

interface DadosExercicioAdministrativo {
    numeroProcesso: string;
    quantidadeDias: number | null;
    justificativa: string;
    declaracao: boolean;
}

interface ParcelaAdministrativaComParcelas {
    id: number | null;
    idParcelaPai: number | null;
    idTemporario: string | null;
    idTemporarioParcelaPai: string | null;
    ordem: number;
    geracao: number;
    dataInicio: string;
    dataFim: Date | null;
    dataInicioCalendarioControl: FormControl<Date | null>;
    quantidadeDias: number | null;
    adiantamentoSalarial: boolean;
    abonoPecuniario: boolean;
    adiantamentoDecimoTerceiro: boolean;
    parcelasDesconto: number | null;
    situacao: string;
    dataInicioDesabilitada: boolean;
    quantidadeDiasDesabilitada: boolean;
    quantidadeDiasAutomatica: boolean;
    selecionadaParaInterrupcao: boolean;
    dataInterrupcao: Date | undefined;
    motivoLegal: string;
    justificativaInterrupcao: string;
    documentoLegal: string;
    dataPublicacaoDocumentoLegal: Date | undefined;
    dataMinimaInterrupcao: Date | null;
    validacao: {
        dataInterrupcaoInvalida: boolean;
        dataInterrupcaoForaPeriodoFerias: boolean;
        dataPublicacaoInvalida: boolean;
        justificativaInterrupcaoInvalida: boolean;
        sobreposto: boolean;
        foraOrdem: boolean;
        foraJanelaProgramacao: boolean;
        quantidadeInsuficienteDias: boolean;
        dataInvalida: boolean;
    };
    tocado: {
        dataInicio: boolean;
        quantidadeDias: boolean;
    };
    dataMinimaReprogramacao: Date | null;
}

type CampoExercicio = keyof DadosExercicioAdministrativo;
type CampoParcela = 'dataInicio' | 'quantidadeDias';

const FORMATO_PROCESSO_ADMINISTRATIVO = /^\d{5}\.\d{6}\/\d{4}-\d{2}$/;
const QUANTIDADE_MAXIMA_DIAS_ADMINISTRATIVOS = 45;
const ROTA_CONSULTAR_EXERCICIOS = '/secure/gerenciar-ferias/consultar-exercicios';

function dataIsoValida(valor: string): boolean {
    const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
    if (!partes) {
        return false;
    }

    const ano = Number(partes[1]);
    const mes = Number(partes[2]);
    const dia = Number(partes[3]);
    const data = new Date(ano, mes - 1, dia);

    return data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;
}

@Component({
    selector: 'app-ferias-especiais-administrativas-com-parcelas',
    templateUrl: './ferias-especiais-administrativas-com-parcelas.component.html',
    styleUrls: ['./ferias-especiais-administrativas-com-parcelas.component.scss']
})
export class FeriasEspeciaisAdministrativasComParcelasComponent extends FeriasBaseComponent implements OnInit {
    readonly dadosExercicio: DadosExercicioAdministrativo = {
        numeroProcesso: '',
        quantidadeDias: null,
        justificativa: '',
        declaracao: false
    };
    readonly parcelas: ParcelaAdministrativaComParcelas[] = [];
    readonly itensWizard: MenuItem[] = [{ label: 'Cadastro Processo' }, { label: 'Alterar Parcelas e Confirmar' }];

    exercicioSelecionado?: ExercicioSelecionado;
    wizardActiveIndex = 0;
    submetidoExercicio = false;
    submetidoParcelas = false;
    houvePreenchimento = false;
    dataInicioPeriodoProgramacaoMinimaCalendario: Date | null = null;
    dataFimPeriodoProgramacaoMaximaCalendario: Date | null = null;

    private readonly camposExercicioTocados = new Set<CampoExercicio>();
    private readonly dadosOriginaisInterrupcao = new WeakMap<
        ParcelaAdministrativaComParcelas,
        {
            quantidadeDias: number;
            dataFim: Date | null;
            situacao: string;
            dataInterrupcaoAplicada: string | null;
        }
    >();
    declaracaoTocada = false;

    constructor(
        private readonly exerciciosService: ExerciciosService,
        alertaMsgService: AlertMessageService,
        private readonly confirmationService: ConfirmationService,
        private readonly router: Router,
        private readonly errorHandler: ErrorHandler,
        private dataFormatadaPipe: DataFormatadaPipe
    ) {
        super(alertaMsgService);
    }

    ngOnInit(): void {
        this.exercicioSelecionado = this.router.getCurrentNavigation()?.extras?.state?.['data'] ?? history.state?.data ?? JSON.parse(sessionStorage.getItem('consultaGerencialExercicioSelecionado') ?? 'null');

        this.dataInicioPeriodoProgramacaoMinimaCalendario = this.paraDataCalendario(this.dataInicioPeriodoProgramacaoMinima);
        this.dataFimPeriodoProgramacaoMaximaCalendario = this.paraDataCalendario(this.dataFimPeriodoProgramacaoMaxima);
        this.dadosExercicio.quantidadeDias = this.exercicioSelecionado?.saldoDias ?? null;
        this.carregarParcelasEditaveis();
    }

    parcelaPodeSerInterrompida(parcela: ParcelaAdministrativaComParcelas): boolean {
        const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcela.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcela.situacao);
        const parcelaPersistidaElegivel = parcela.id !== null && (situacao === SituacaoParcelaFeriasEnum.USUFRUIDA || situacao === SituacaoParcelaFeriasEnum.EM_GOZO);
        const parcelaNovaElegivel = parcela.id === null && parcela.geracao > 0 && parcela.idTemporario !== null;

        return parcela.quantidadeDias !== null && parcela.quantidadeDias > 1 && dataIsoValida(parcela.dataInicio) && !this.possuiParcelaFilha(parcela) && (parcelaPersistidaElegivel || parcelaNovaElegivel);
    }

    selecionarParcelaParaInterromper(parcela: ParcelaAdministrativaComParcelas): void {
        if (!this.parcelaPodeSerInterrompida(parcela)) {
            return;
        }

        this.houvePreenchimento = true;
        parcela.selecionadaParaInterrupcao = true;
        this.dadosOriginaisInterrupcao.set(parcela, {
            quantidadeDias: parcela.quantidadeDias,
            dataFim: parcela.dataFim ? new Date(parcela.dataFim) : null,
            situacao: parcela.situacao,
            dataInterrupcaoAplicada: null
        });
        this.criarParcelaFilha(parcela);
    }

    private possuiParcelaFilha(parcela: ParcelaAdministrativaComParcelas): boolean {
        return this.parcelas.some(filha => this.ehParcelaFilha(parcela, filha));
    }

    private ehParcelaFilha(parcela: ParcelaAdministrativaComParcelas, filha: ParcelaAdministrativaComParcelas): boolean {
        if (filha.geracao !== parcela.geracao + 1) {
            return false;
        }

        return parcela.id !== null ? filha.idParcelaPai === parcela.id : parcela.idTemporario !== null && filha.idTemporarioParcelaPai === parcela.idTemporario;
    }

    private parcelaFilhaPendente(parcela: ParcelaAdministrativaComParcelas): ParcelaAdministrativaComParcelas | undefined {
        return this.parcelas.find(filha => filha.id === null && this.ehParcelaFilha(parcela, filha));
    }

    private parcelaPaiInterrompida(parcela: ParcelaAdministrativaComParcelas): ParcelaAdministrativaComParcelas | undefined {
        return this.parcelas.find(parcelaPai => {
            if (!this.ehParcelaFilha(parcelaPai, parcela)) {
                return false;
            }

            const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcelaPai.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcelaPai.situacao);
            return situacao === SituacaoParcelaFeriasEnum.INTERROMPIDA;
        });
    }

    private permiteExcluirParcelaFilhaInterrompida(parcela: ParcelaAdministrativaComParcelas): boolean {
        if (parcela.geracao === 0 || parcela.id === null || parcela.selecionadaParaInterrupcao || this.possuiParcelaFilha(parcela)) {
            return false;
        }

        const parcelaPai = this.parcelaPaiInterrompida(parcela);
        return parcelaPai !== undefined && !parcelaPai.selecionadaParaInterrupcao && !this.parcelas.some(outraParcela => outraParcela !== parcela && this.ehParcelaFilha(parcelaPai, outraParcela));
    }

    private criarParcelaFilha(parcela: ParcelaAdministrativaComParcelas): void {
        const indicePai = this.parcelas.indexOf(parcela);
        if ((parcela.id === null && parcela.idTemporario === null) || indicePai === -1 || this.parcelaFilhaPendente(parcela)) {
            return;
        }

        const parcelaFilha = this.criarParcela({
            idParcelaPai: parcela.id,
            idTemporarioParcelaPai: parcela.id === null ? parcela.idTemporario : null,
            ordem: parcela.ordem,
            geracao: parcela.geracao + 1,
            dataInicio: '',
            quantidadeDias: null,
            quantidadeDiasAutomatica: true,
            adiantamentoSalarial: false,
            abonoPecuniario: false,
            adiantamentoDecimoTerceiro: false,
            situacao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao
        });
        this.parcelas.splice(indicePai + 1, 0, parcelaFilha);
    }

    private excluirParcelaFilha(parcela: ParcelaAdministrativaComParcelas): void {
        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        if (!parcelaFilha) {
            return;
        }

        this.excluirParcelaFilha(parcelaFilha);
        this.dadosOriginaisInterrupcao.delete(parcelaFilha);
        const indiceParcelaFilha = this.parcelas.indexOf(parcelaFilha);
        if (indiceParcelaFilha !== -1) {
            this.parcelas.splice(indiceParcelaFilha, 1);
        }
    }

    private restaurarDadosOriginaisInterrupcao(parcela: ParcelaAdministrativaComParcelas): void {
        const dadosOriginais = this.dadosOriginaisInterrupcao.get(parcela);
        if (!dadosOriginais) {
            return;
        }

        parcela.quantidadeDias = dadosOriginais.quantidadeDias;
        parcela.dataFim = dadosOriginais.dataFim ? new Date(dadosOriginais.dataFim) : null;
        parcela.situacao = dadosOriginais.situacao;

        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        if (parcelaFilha) {
            parcelaFilha.quantidadeDias = null;
            parcelaFilha.dataFim = null;
            parcelaFilha.dataMinimaReprogramacao = this.calcularDataMinimaReprogramacao();
        }
    }

    desistirSelecaoParcelaParaInterromper(parcela: ParcelaAdministrativaComParcelas): void {
        this.restaurarDadosOriginaisInterrupcao(parcela);
        this.excluirParcelaFilha(parcela);
        this.dadosOriginaisInterrupcao.delete(parcela);
        this.limparDadosInterrupcao(parcela);
    }

    private limparDadosInterrupcao(parcela: ParcelaAdministrativaComParcelas): void {
        parcela.motivoLegal = '';
        parcela.justificativaInterrupcao = '';
        parcela.dataInterrupcao = undefined;
        parcela.documentoLegal = '';
        parcela.dataPublicacaoDocumentoLegal = undefined;
        parcela.selecionadaParaInterrupcao = false;
        parcela.validacao.justificativaInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        parcela.validacao.dataPublicacaoInvalida = false;
    }

    get msgJustificativaObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_OBRIGATORIA;
    }

    get msgJustificativaInvalida(): string {
        return Message.ERRO_JUSTIFICATIVA_TAMANHO_INVALIDO;
    }

    get quantidadeMaximaParcelas(): number {
        return this.exercicioSelecionado?.quantidadeMaximaParcelas ?? 6;
    }

    get quantidadeMaximaDiasParcela(): number {
        return this.dadosExercicio.quantidadeDias ?? QUANTIDADE_MAXIMA_DIAS_ADMINISTRATIVOS;
    }

    get dataInicioPeriodoProgramacaoMinima(): string {
        return this.paraDataIso(this.exercicioSelecionado?.dataInicioPeriodoProgramacaoDisponivel || this.exercicioSelecionado?.dataInicioPeriodoProgramacao);
    }

    get dataFimPeriodoProgramacaoMaxima(): string {
        return this.paraDataIso(this.exercicioSelecionado?.dataFimPeriodoProgramacaoDisponivel || this.exercicioSelecionado?.dataFimPeriodoProgramacao);
    }

    get exibeAbonoPecuniario(): boolean {
        return this.exercicioSelecionado?.siglaRegime === SituacaoFuncionalEnum.CLT.id;
    }

    get umTercoSaldoFerias(): number {
        return Math.ceil((this.exercicioSelecionado?.saldoDias ?? 0) / 3);
    }

    get totalDiasParcelas(): number {
        return this.parcelas.reduce((total, parcela) => {
            const diasParcela = parcela.quantidadeDias ?? 0;
            const diasAbono = parcela.abonoPecuniario ? this.umTercoSaldoFerias : 0;
            return total + diasParcela + diasAbono;
        }, 0);
    }

    get alocacaoDiasIncompleta(): boolean {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        return quantidadeDiasExercicio !== null && this.totalDiasParcelas !== quantidadeDiasExercicio;
    }

    get existemParcelasSobrepostas(): boolean {
        return this.parcelas.some((_, indice) => this.parcelaSobreposta(indice));
    }

    get existeParcelaForaDeOrdem(): boolean {
        return this.parcelas.some((_, indice) => this.parcelaForaDaOrdem(indice));
    }

    get existeParcelaComQuantidadeDiasIgualZero(): boolean {
        return this.parcelas.some(parcela => !parcela.quantidadeDiasDesabilitada && parcela.quantidadeDias === 0);
    }

    get existeCampoObrigatorioNaoInformado(): boolean {
        if (this.campoDeclaracaoNaoInformado()) {
            return true;
        }

        return this.parcelas.some(parcela => !parcela.dataInicio || (!parcela.quantidadeDiasDesabilitada && parcela.quantidadeDias === null));
    }

    private get existeInterrupcaoInvalida(): boolean {
        return this.parcelas.some(parcela => {
            if (!parcela.selecionadaParaInterrupcao) {
                return false;
            }

            const motivoLegal = parcela.motivoLegal.trim();
            const justificativa = parcela.justificativaInterrupcao.trim();
            const dataInterrupcao = parcela.dataInterrupcao;
            const dataPublicacao = parcela.dataPublicacaoDocumentoLegal;
            const dataMaximaInterrupcao = this.dataMaximaInterrupcao(parcela);
            const dataInterrupcaoForaPeriodo = !!dataInterrupcao && !Number.isNaN(dataInterrupcao.getTime()) && ((parcela.dataMinimaInterrupcao !== null && dataInterrupcao < parcela.dataMinimaInterrupcao) || (dataMaximaInterrupcao !== null && dataInterrupcao > dataMaximaInterrupcao));
            const justificativaInvalida = parcela.validacao.justificativaInterrupcaoInvalida || (justificativa.length > 0 && (justificativa.length < 10 || justificativa.length > 300));

            return !dataInterrupcao || Number.isNaN(dataInterrupcao.getTime()) || dataInterrupcaoForaPeriodo || parcela.validacao.dataInterrupcaoInvalida || parcela.validacao.dataInterrupcaoForaPeriodoFerias || !motivoLegal || !parcela.documentoLegal.trim() || !dataPublicacao || Number.isNaN(dataPublicacao.getTime()) || parcela.validacao.dataPublicacaoInvalida || justificativaInvalida || (motivoLegal === 'Necessidade do Serviço' && justificativa.length === 0);
        });
    }

    get msgQuantidadeDiasIgualZero(): string {
        return Message.ERRO_DURACAO_MINIMA_PARCELA;
    }

    get msgAlocacaoSaldoDiasIncompleta(): string {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        return formatarMensagem(Message.ERRO_SOMA_PARCELAS_DIFERENTE_DO_SALDO, {
            totalDias: this.totalDiasParcelas.toString(),
            saldoDias: (quantidadeDiasExercicio ?? 0).toString()
        });
    }

    get msgCamposObrigatoriosNaoPreenchidos(): string {
        return Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS;
    }

    private get parcelasOriginaisEditaveis(): {
        parcela: ParcelaFerias;
        geracao: number;
        idParcelaPai: number | null;
    }[] {
        const parcelas: {
            parcela: ParcelaFerias;
            geracao: number;
            idParcelaPai: number | null;
        }[] = [];

        const achatarParcelas = (parcela: ParcelaFerias, geracao: number, idParcelaPai: number | null = null): void => {
            if (this.ehParcelaCancelada(parcela)) {
                return;
            }

            parcelas.push({
                parcela,
                geracao,
                idParcelaPai: parcela.idParcelaPai ?? idParcelaPai
            });

            if (parcela.parcelaFilha) {
                achatarParcelas(parcela.parcelaFilha, geracao + 1, parcela.id);
            }
        };

        (this.exercicioSelecionado?.parcelas ?? []).forEach(parcela => achatarParcelas(parcela, 0));

        return parcelas;
    }

    campoExercicioInvalido(nome: CampoExercicio): boolean {
        return this.campoExercicioTemErro(nome) && (this.camposExercicioTocados.has(nome) || this.submetidoExercicio);
    }

    campoDeclaracaoNaoInformado() {
        return !this.dadosExercicio.declaracao;
    }

    campoExercicioObrigatorioNaoInformado(nome: CampoExercicio): boolean {
        if (!this.campoExercicioInvalido(nome)) {
            return false;
        }

        switch (nome) {
            case 'numeroProcesso':
                return this.dadosExercicio.numeroProcesso.trim().length === 0;
            case 'quantidadeDias':
                return this.dadosExercicio.quantidadeDias === null;
            case 'justificativa':
                return this.dadosExercicio.justificativa.trim().length === 0;
        }
    }

    private get existeCampoExercicioObrigatorioNaoInformado(): boolean {
        return this.campoExercicioObrigatorioNaoInformado('numeroProcesso') || this.campoExercicioObrigatorioNaoInformado('quantidadeDias') || this.campoExercicioObrigatorioNaoInformado('justificativa');
    }

    campoParcelaInvalido(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela || (!parcela.tocado[nome] && !this.submetidoParcelas)) {
            return false;
        }

        return this.campoParcelaTemErro(indice, nome) || (nome === 'dataInicio' && (this.parcelaSobreposta(indice) || this.parcelaForaDaOrdem(indice)));
    }

    campoParcelaObrigatorioNaoInformado(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela || !this.campoParcelaInvalido(indice, nome)) {
            return false;
        }

        return nome === 'dataInicio' ? parcela.dataInicio.length === 0 : !parcela.quantidadeDiasDesabilitada && parcela.quantidadeDias === null;
    }

    parcelaInvalida(indice: number, nome: CampoParcela): boolean {
        return this.campoParcelaTemErro(indice, nome);
    }

    private campoExercicioTemErro(nome: CampoExercicio): boolean {
        switch (nome) {
            case 'numeroProcesso':
                return !FORMATO_PROCESSO_ADMINISTRATIVO.test(this.dadosExercicio.numeroProcesso);
            case 'quantidadeDias': {
                const quantidade = this.dadosExercicio.quantidadeDias;
                return quantidade === null || !Number.isInteger(quantidade) || quantidade < 1 || quantidade > QUANTIDADE_MAXIMA_DIAS_ADMINISTRATIVOS;
            }
            case 'justificativa': {
                const justificativa = this.dadosExercicio.justificativa.trim();
                return justificativa.length < 10 || justificativa.length > 300;
            }
            case 'declaracao':
                return !this.dadosExercicio.declaracao;
        }
    }

    get exercicioInvalido(): boolean {
        return this.campoExercicioTemErro('numeroProcesso') || this.campoExercicioTemErro('quantidadeDias') || this.campoExercicioTemErro('justificativa');
    }

    private campoParcelaTemErro(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return false;
        }

        if (nome === 'dataInicio') {
            return !parcela.dataInicioDesabilitada && (!parcela.dataInicio || !dataIsoValida(parcela.dataInicio) || this.periodoProgramacaoIndisponivel(indice) || this.parcelaForaPeriodoProgramacao(indice));
        }

        return !parcela.quantidadeDiasDesabilitada && (parcela.quantidadeDias === null || !Number.isInteger(parcela.quantidadeDias) || parcela.quantidadeDias < 1 || parcela.quantidadeDias > this.quantidadeMaximaDiasParcela);
    }

    periodoProgramacaoIndisponivel(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela?.dataInicio || !dataIsoValida(parcela.dataInicio)) {
            return false;
        }

        return !this.dataInicioPeriodoProgramacaoMinima || !this.dataFimPeriodoProgramacaoMaxima;
    }

    parcelaForaPeriodoProgramacao(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela?.dataInicio || !dataIsoValida(parcela.dataInicio)) {
            return false;
        }

        const inicioPeriodo = this.dataInicioPeriodoProgramacaoMinima;
        const fimPeriodo = this.dataFimPeriodoProgramacaoMaxima;
        return !!inicioPeriodo && !!fimPeriodo && (parcela.dataInicio < inicioPeriodo || parcela.dataInicio > fimPeriodo);
    }

    marcarDeclaracaoTocada(): void {
        this.declaracaoTocada = true;
    }

    marcarCampoExercicioTocado(nome: CampoExercicio): void {
        this.camposExercicioTocados.add(nome);
    }

    marcarCampoParcelaTocado(indice: number, nome: CampoParcela): void {
        const parcela = this.parcelas[indice];
        if (parcela) {
            parcela.tocado[nome] = true;
        }
    }

    registrarAlteracao(): void {
        this.houvePreenchimento = true;
    }

    validarInputQuantidadeDias(event: Event): void {
        const input = event.target as HTMLInputElement;
        const valor = input.value.replace(/\D/g, '').slice(0, 2);
        input.value = valor;

        this.dadosExercicio.quantidadeDias = valor === '' ? null : Number(valor);
        this.registrarAlteracao();
    }

    dataFimParcela(indice: number): string {
        const parcela = this.parcelas[indice];
        const dataFim = parcela ? this.calcularDataFimParcela(parcela.dataInicio, parcela.quantidadeDias) : null;
        return dataFim ? this.paraDataIso(dataFim) : '';
    }

    private calcularDataFimParcela(dataInicio: string, quantidadeDias: number | null): Date | null {
        if (!dataIsoValida(dataInicio) || quantidadeDias === null || !Number.isInteger(quantidadeDias) || quantidadeDias < 1) {
            return null;
        }

        const [ano, mes, dia] = dataInicio.split('-').map(Number);
        return new Date(ano, mes - 1, dia + quantidadeDias - 1);
    }

    private calcularDataMinimaInterrupcao(dataInicio: string): Date | null {
        const data = this.paraDataCalendario(dataInicio);
        return data ? new Date(data.getFullYear(), data.getMonth(), data.getDate() + 1) : null;
    }

    private calcularDataMinimaReprogramacao(dataInterrupcao?: Date): Date | null {
        const dataInicioMinima = this.dataInicioPeriodoProgramacaoMinimaCalendario;
        if (!dataInterrupcao) {
            return dataInicioMinima;
        }

        const dataPosteriorInterrupcao = new Date(dataInterrupcao.getFullYear(), dataInterrupcao.getMonth(), dataInterrupcao.getDate() + 1);
        return dataInicioMinima && dataInicioMinima > dataPosteriorInterrupcao ? dataInicioMinima : dataPosteriorInterrupcao;
    }

    private atualizarDataFimParcela(indice: number): void {
        const parcela = this.parcelas[indice];
        if (parcela) {
            parcela.dataFim = this.calcularDataFimParcela(parcela.dataInicio, parcela.quantidadeDias);
        }
    }

    alterarDataInicioParcela(indice: number): void {
        if (this.parcelas[indice]?.dataInicioDesabilitada) {
            return;
        }

        this.registrarAlteracao();
        this.criarParcelaParaDiasRestantes();
    }

    atualizarDataInicioParcela(indice: number, dataInicio: Date | null): void {
        const parcela = this.parcelas[indice];
        if (!parcela || parcela.dataInicioDesabilitada) {
            return;
        }

        const dataInicioIso = dataInicio ? this.paraDataIso(dataInicio) : '';
        if (dataInicioIso && !dataIsoValida(dataInicioIso)) {
            return;
        }

        const dataAlterada = parcela.dataInicio !== dataInicioIso;
        parcela.dataInicioCalendarioControl.setValue(dataInicio, {
            emitEvent: false
        });
        parcela.dataInicio = dataInicioIso;
        parcela.dataMinimaInterrupcao = this.calcularDataMinimaInterrupcao(dataInicioIso);
        this.atualizarDataFimParcela(indice);
        if (dataAlterada) {
            this.alterarDataInicioParcela(indice);
        } else {
            this.registrarAlteracao();
        }
    }

    finalizarDataInicioParcela(indice: number, event: Event): void {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return;
        }

        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataInicioDigitada = getValidDateDDMMYYYY(valorDigitado);
        const dataInicioIso = dataInicioDigitada ? this.paraDataIso(dataInicioDigitada) : '';
        const dataInicioCalendarioIso = parcela.dataInicioCalendarioControl.value ? this.paraDataIso(parcela.dataInicioCalendarioControl.value) : '';

        if (parcela.dataInicio !== dataInicioIso || dataInicioCalendarioIso !== dataInicioIso) {
            this.atualizarDataInicioParcela(indice, dataInicioDigitada);
        }
        this.marcarCampoParcelaTocado(indice, 'dataInicio');
    }

    atualizarQuantidadeDiasParcela(indice: number, event: Event): void {
        const parcela = this.parcelas[indice];
        if (!parcela || parcela.quantidadeDiasDesabilitada) {
            return;
        }

        const valor = (event.target as HTMLInputElement).value;
        parcela.quantidadeDias = valor === '' ? null : Number(valor);
        this.atualizarDataFimParcela(indice);
        this.registrarAlteracao();
    }

    alterarQuantidadeDiasParcela(indice: number): void {
        if (this.parcelas[indice]?.quantidadeDiasDesabilitada) {
            return;
        }

        this.registrarAlteracao();
        this.criarParcelaParaDiasRestantes();
    }

    permiteExcluirParcela(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return false;
        }

        return this.permiteExcluirParcelaFilhaInterrompida(parcela) || (this.quantidadeOrdensParcelas > 1 && parcela.geracao === 0 && !parcela.selecionadaParaInterrupcao && !this.possuiParcelaFilha(parcela) && !parcela.dataInicioDesabilitada && this.indiceUltimaParcelaRedistribuivel(indice) !== -1);
    }

    confirmarExcluirParcela(indice: number): void {
        if (!this.permiteExcluirParcela(indice)) {
            return;
        }

        this.confirmationService.confirm({
            key: 'confirmPadrao',
            header: 'Confirmação de exclusão',
            message: Message.CONFIRMACAO_EXCLUIR_PARCELA,
            accept: () => this.excluirParcela(indice)
        });
    }

    excluirParcela(indice: number): void {
        if (!this.permiteExcluirParcela(indice)) {
            return;
        }

        const parcelaExcluida = this.parcelas[indice];
        if (parcelaExcluida.geracao > 0) {
            this.excluirParcelaFilhaInterrompida(indice);
            return;
        }

        const diasExcluidos = (parcelaExcluida.quantidadeDias ?? 0) + (parcelaExcluida.abonoPecuniario ? this.umTercoSaldoFerias : 0);
        const indiceRedistribuicao = this.indiceUltimaParcelaRedistribuivel(indice);
        const indiceRedistribuicaoFinal = indiceRedistribuicao > indice ? indiceRedistribuicao - 1 : indiceRedistribuicao;

        this.parcelas.splice(indice, 1);
        const ordensRestantes = [...new Set(this.parcelas.map(parcela => parcela.ordem))].sort((a, b) => a - b);
        const ordemAtualizada = new Map(ordensRestantes.map((ordem, posicao) => [ordem, posicao + 1]));
        this.parcelas.forEach(parcela => {
            parcela.ordem = ordemAtualizada.get(parcela.ordem) ?? parcela.ordem;
        });
        const parcelaRedistribuicao = this.parcelas[indiceRedistribuicaoFinal];
        parcelaRedistribuicao.quantidadeDias = (parcelaRedistribuicao.quantidadeDias ?? 0) + diasExcluidos;
        this.atualizarDataFimParcela(indiceRedistribuicaoFinal);
        this.registrarAlteracao();
    }

    private excluirParcelaFilhaInterrompida(indice: number): void {
        const parcelaFilha = this.parcelas[indice];
        const parcelaPai = parcelaFilha && this.parcelaPaiInterrompida(parcelaFilha);
        const indiceParcelaPai = parcelaPai ? this.parcelas.indexOf(parcelaPai) : -1;
        if (!parcelaFilha || !parcelaPai || !this.permiteExcluirParcelaFilhaInterrompida(parcelaFilha) || indiceParcelaPai === -1) {
            return;
        }

        parcelaPai.quantidadeDias = (parcelaPai.quantidadeDias ?? 0) + (parcelaFilha.quantidadeDias ?? 0) + (parcelaFilha.abonoPecuniario ? this.umTercoSaldoFerias : 0);
        this.atualizarDataFimParcela(indiceParcelaPai);
        parcelaPai.situacao = this.situacaoAposRecomporParcela(parcelaPai.dataFim);
        this.limparDadosInterrupcao(parcelaPai);
        this.dadosOriginaisInterrupcao.delete(parcelaPai);

        const permiteEditar = this.situacaoPermiteEdicao(parcelaPai.situacao);
        parcelaPai.dataInicioDesabilitada = !permiteEditar;
        if (permiteEditar) {
            parcelaPai.dataInicioCalendarioControl.enable({ emitEvent: false });
        } else {
            parcelaPai.dataInicioCalendarioControl.disable({ emitEvent: false });
        }
        parcelaPai.quantidadeDiasDesabilitada = !permiteEditar || parcelaPai.quantidadeDiasAutomatica;

        this.parcelas.splice(indice, 1);
        this.registrarAlteracao();
    }

    private situacaoAposRecomporParcela(dataFim: Date | null): string {
        if (!dataFim) {
            return SituacaoParcelaFeriasEnum.USUFRUIDA.descricao;
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const dataFimSemHora = new Date(dataFim);
        dataFimSemHora.setHours(0, 0, 0, 0);

        return hoje > dataFimSemHora ? SituacaoParcelaFeriasEnum.USUFRUIDA.descricao : SituacaoParcelaFeriasEnum.EM_GOZO.descricao;
    }

    parcelaSobreposta(indice: number): boolean {
        const intervalo = this.intervaloParcela(indice);
        if (!intervalo) {
            return false;
        }

        return this.parcelas.some((_, outroIndice) => {
            if (outroIndice === indice) {
                return false;
            }

            const outroIntervalo = this.intervaloParcela(outroIndice);
            return outroIntervalo !== null && intervalo.inicio <= outroIntervalo.fim && outroIntervalo.inicio <= intervalo.fim;
        });
    }

    parcelaForaDaOrdem(indice: number): boolean {
        const intervalo = this.intervaloParcela(indice);
        if (!intervalo) {
            return false;
        }

        for (let indiceAnterior = indice - 1; indiceAnterior >= 0; indiceAnterior--) {
            const intervaloAnterior = this.intervaloParcela(indiceAnterior);
            if (intervaloAnterior) {
                return intervalo.inicio <= intervaloAnterior.inicio;
            }
        }

        return false;
    }

    avancarWizard(): void {
        this.submetidoExercicio = true;
        this.clearMessages();

        if (this.exercicioInvalido || !this.exercicioSelecionado) {
            if (this.existeCampoExercicioObrigatorioNaoInformado) {
                this.getAlertaMsgService().error(this.msgCamposObrigatoriosNaoPreenchidos);
            }
            return;
        }

        this.wizardActiveIndex = 1;
        this.rolarParaTopo();
    }

    voltarWizard(): void {
        this.wizardActiveIndex = 0;
        this.rolarParaTopo();
    }

    concluirWizard(): void {
        this.clearMessages();
        this.submetidoExercicio = true;
        this.submetidoParcelas = true;
        let podeConcluir = true;
        if (this.existeParcelaComQuantidadeDiasIgualZero) {
            this.getAlertaMsgService().error(this.msgQuantidadeDiasIgualZero);
            podeConcluir = false;
        }

        if (this.alocacaoDiasIncompleta) {
            this.getAlertaMsgService().error(this.msgAlocacaoSaldoDiasIncompleta);
            podeConcluir = false;
        }

        if (this.existeCampoObrigatorioNaoInformado || this.existeInterrupcaoInvalida) {
            this.getAlertaMsgService().error(this.msgCamposObrigatoriosNaoPreenchidos);
            podeConcluir = false;
        }

        if (this.existemParcelasSobrepostas) {
            this.getAlertaMsgService().error(Message.ERRO_PARCELAS_COM_DATAS_SOBREPOSTAS);
            podeConcluir = false;
        }

        if (this.existeParcelaForaDeOrdem) {
            this.getAlertaMsgService().error(Message.ERRO_DATA_INICIO_PARCELA_DEVE_SER_POSTERIOR);
            podeConcluir = false;
        }

        if (this.exercicioInvalido || !this.exercicioSelecionado) {
            this.wizardActiveIndex = 0;
            return;
        }

        const existeParcelaInvalida = this.parcelas.some((_, indice) => this.campoParcelaTemErro(indice, 'dataInicio') || this.campoParcelaTemErro(indice, 'quantidadeDias'));
        if (existeParcelaInvalida || this.parcelas.length === 0) {
            return;
        }

        if (!podeConcluir) {
            return;
        }

        const dados = this.montarPayload();
        if (!dados) {
            return;
        }

        this.exerciciosService.feriasEspeciaisAdministrativasComParcelas(this.exercicioSelecionado.idFerias, dados).subscribe({
            next: () => {
                this.getAlertaMsgService().setAwatingSuccessMessage('Férias Especiais Administrativas com Parcelas registradas com sucesso.');
                this.router.navigate([ROTA_CONSULTAR_EXERCICIOS]);
            },
            error: error => this.errorHandler.handleError(error)
        });
    }

    confirmarCancelarWizard() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar(): void {
        this.router.navigate([ROTA_CONSULTAR_EXERCICIOS]);
    }

    private criarParcelaParaDiasRestantes(): void {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        if (quantidadeDiasExercicio === null || this.totalDiasParcelas <= 0 || this.totalDiasParcelas >= quantidadeDiasExercicio || !this.permiteIncluirParcelaAutomaticamente) {
            return;
        }

        this.parcelas.push(
            this.criarParcela({
                ordem: this.proximaOrdemParcela,
                dataInicio: '',
                quantidadeDias: quantidadeDiasExercicio - this.totalDiasParcelas,
                adiantamentoSalarial: false,
                abonoPecuniario: false,
                adiantamentoDecimoTerceiro: false,
                situacao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao
            })
        );
        this.registrarAlteracao();
    }

    private get permiteIncluirParcelaAutomaticamente(): boolean {
        return this.quantidadeOrdensParcelas < this.quantidadeMaximaParcelas;
    }

    private get quantidadeOrdensParcelas(): number {
        return new Set(this.parcelas.map(parcela => parcela.ordem)).size;
    }

    private get proximaOrdemParcela(): number {
        return this.parcelas.reduce((maiorOrdem, parcela) => Math.max(maiorOrdem, parcela.ordem), 0) + 1;
    }

    private indiceUltimaParcelaRedistribuivel(indiceExcluido: number): number {
        for (let indice = this.parcelas.length - 1; indice >= 0; indice--) {
            if (indice !== indiceExcluido && !this.possuiParcelaFilha(this.parcelas[indice]) && !this.parcelas[indice].dataInicioDesabilitada) {
                return indice;
            }
        }

        return -1;
    }

    private intervaloParcela(indice: number): { inicio: string; fim: string } | null {
        const parcela = this.parcelas[indice];
        const dataInicio = parcela?.dataInicio;
        const quantidadeDias = parcela?.quantidadeDias;

        if (!dataInicio || !dataIsoValida(dataInicio) || quantidadeDias === null || quantidadeDias < 1) {
            return null;
        }

        const dataFim = this.dataFimParcela(indice);
        return dataFim ? { inicio: dataInicio, fim: dataFim } : null;
    }

    private montarPayload(): FeriasEspeciaisAdministrativasComParcelas | null {
        const valores = this.dadosExercicio;
        const exercicio = this.exercicioSelecionado;
        const periodoAquisitivo = exercicio?.periodoAquisitivo;
        const extraordinario = exercicio?.extraordinario === true;

        if (valores.quantidadeDias === null || !exercicio || !this.ehPeriodoAquisitivo(periodoAquisitivo)) {
            this.submetidoExercicio = true;
            if (exercicio && !this.ehPeriodoAquisitivo(periodoAquisitivo)) {
                this.getAlertaMsgService().error('O tipo de período aquisitivo do exercício é inválido.');
            }
            return null;
        }

        if (extraordinario && !exercicio.dataFimPeriodoProgramacao) {
            this.getAlertaMsgService().error('O exercício extraordinário não possui fim do período de programação.');
            return null;
        }

        const dados: FeriasEspeciaisAdministrativasComParcelas = {
            numeroProcesso: valores.numeroProcesso.trim(),
            quantidadeDias: valores.quantidadeDias,
            justificativa: valores.justificativa.trim(),
            periodoAquisitivo,
            extraordinario,
            parcelas: this.parcelas.map(parcela => this.montarParcela(parcela))
        };

        if (extraordinario) {
            dados.dataFimPeriodoProgramacao = exercicio.dataFimPeriodoProgramacao;
        }

        return dados;
    }

    private montarParcela(parcela: ParcelaAdministrativaComParcelas): ParcelaFeriasEspecialAdministrativaComParcelas {
        const dados: ParcelaFeriasEspecialAdministrativaComParcelas = {
            geracao: parcela.geracao,
            ordem: parcela.ordem,
            dataInicio: parcela.dataInicio,
            quantidadeDias: parcela.quantidadeDias ?? 0,
            adiantamentoSalarial: parcela.adiantamentoSalarial,
            abonoPecuniario: parcela.abonoPecuniario,
            adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro
        };

        if (parcela.parcelasDesconto !== null) {
            dados.parcelasDesconto = parcela.parcelasDesconto;
        }

        if (parcela.id !== null) {
            dados.id = parcela.id;
        }

        if (parcela.idParcelaPai !== null) {
            dados.idParcelaPai = parcela.idParcelaPai;
        }

        if (parcela.idTemporario !== null) {
            dados.idTemporario = parcela.idTemporario;
        }

        if (parcela.idTemporarioParcelaPai !== null) {
            dados.idTemporarioParcelaPai = parcela.idTemporarioParcelaPai;
        }

        const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcela.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcela.situacao);
        if (parcela.selecionadaParaInterrupcao || situacao === SituacaoParcelaFeriasEnum.INTERROMPIDA) {
            dados.dataInterrupcao = parcela.dataInterrupcao;
            dados.motivoLegal = parcela.motivoLegal;
            dados.justificativaInterrupcao = parcela.justificativaInterrupcao;
            dados.documentoLegal = parcela.documentoLegal;
            dados.dataPublicacaoDocumentoLegal = parcela.dataPublicacaoDocumentoLegal;
        }

        return dados;
    }

    private carregarParcelasEditaveis(): void {
        this.parcelas.length = 0;
        this.parcelas.push(
            ...this.parcelasOriginaisEditaveis.map(({ parcela, geracao, idParcelaPai }, indice) =>
                this.criarParcela({
                    id: parcela.id ?? null,
                    idParcelaPai,
                    ordem: parcela.ordem ?? indice + 1,
                    geracao,
                    dataInicio: this.paraDataIso(parcela.dataInicio),
                    dataFim: parcela.dataFim,
                    quantidadeDias: parcela.quantidadeDias ?? null,
                    adiantamentoSalarial: parcela.adiantamentoSalarial === true,
                    abonoPecuniario: parcela.abonoPecuniario === true,
                    adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro === true,
                    parcelasDesconto: parcela.parcelasDesconto ?? null,
                    situacao: parcela.situacao ?? '',
                    dataInterrupcao: parcela.dataInterrupcao,
                    motivoLegal: parcela.motivoLegal ?? '',
                    justificativaInterrupcao: parcela.justificativaInterrupcao ?? '',
                    documentoLegal: parcela.documentoLegal?.toString() ?? '',
                    dataPublicacaoDocumentoLegal: parcela.dataPublicacaoDocumentoLegal
                })
            )
        );
        this.parcelas.sort((a, b) => {
            if (a.ordem === b.ordem) {
                return a.geracao - b.geracao;
            }
            return a.ordem - b.ordem;
        });
    }

    private criarParcela(valores: {
        id?: number | null;
        idParcelaPai?: number | null;
        idTemporario?: string | null;
        idTemporarioParcelaPai?: string | null;
        ordem: number;
        geracao?: number;
        dataInicio?: string;
        dataFim?: Date | string;
        quantidadeDias?: number | null;
        adiantamentoSalarial?: boolean;
        abonoPecuniario?: boolean;
        adiantamentoDecimoTerceiro?: boolean;
        parcelasDesconto?: number | null;
        quantidadeDiasAutomatica?: boolean;
        situacao?: string;
        dataInterrupcao?: Date;
        motivoLegal?: string;
        justificativaInterrupcao?: string;
        documentoLegal?: string;
        dataPublicacaoDocumentoLegal?: Date;
    }): ParcelaAdministrativaComParcelas {
        const parcelaExistente = valores.id !== undefined && valores.id !== null;
        const quantidadeDiasAutomatica = valores.quantidadeDiasAutomatica === true;
        const podeEditar = !parcelaExistente || this.situacaoPermiteEdicao(valores.situacao ?? '');
        const dataInicio = valores.dataInicio ?? '';
        const quantidadeDias = valores.quantidadeDias ?? null;
        const dataFim = this.paraDataCalendario(this.paraDataIso(valores.dataFim)) ?? this.calcularDataFimParcela(dataInicio, quantidadeDias);
        const dataInicioCalendario = this.paraDataCalendario(dataInicio);
        const dataInicioDesabilitada = !podeEditar;

        return {
            id: valores.id ?? null,
            idParcelaPai: valores.idParcelaPai ?? null,
            idTemporario: parcelaExistente ? null : (valores.idTemporario ?? generateUID()),
            idTemporarioParcelaPai: valores.idTemporarioParcelaPai ?? null,
            ordem: valores.ordem,
            geracao: valores.geracao ?? 0,
            dataInicio,
            dataFim,
            dataInicioCalendarioControl: new FormControl<Date | null>({
                value: dataInicioCalendario,
                disabled: dataInicioDesabilitada
            }),
            quantidadeDias,
            adiantamentoSalarial: valores.adiantamentoSalarial === true,
            abonoPecuniario: valores.abonoPecuniario === true,
            adiantamentoDecimoTerceiro: valores.adiantamentoDecimoTerceiro === true,
            parcelasDesconto: valores.parcelasDesconto ?? null,
            situacao: valores.situacao ?? '',
            dataInicioDesabilitada,
            quantidadeDiasDesabilitada: !podeEditar || quantidadeDiasAutomatica,
            quantidadeDiasAutomatica,
            selecionadaParaInterrupcao: false,
            dataInterrupcao: valores.dataInterrupcao,
            motivoLegal: valores.motivoLegal ?? '',
            justificativaInterrupcao: valores.justificativaInterrupcao ?? '',
            documentoLegal: valores.documentoLegal ?? '',
            dataPublicacaoDocumentoLegal: valores.dataPublicacaoDocumentoLegal,
            validacao: {
                dataInterrupcaoInvalida: false,
                dataInterrupcaoForaPeriodoFerias: false,
                dataPublicacaoInvalida: false,
                justificativaInterrupcaoInvalida: false,
                sobreposto: false,
                foraOrdem: false,
                foraJanelaProgramacao: false,
                quantidadeInsuficienteDias: false,
                dataInvalida: false
            },
            tocado: {
                dataInicio: false,
                quantidadeDias: false
            },
            dataMinimaInterrupcao: this.calcularDataMinimaInterrupcao(dataInicio),
            dataMinimaReprogramacao: this.calcularDataMinimaReprogramacao()
        };
    }

    private situacaoPermiteEdicao(situacao: string): boolean {
        const valor = SituacaoParcelaFeriasEnum.fromDesc(situacao) ?? SituacaoParcelaFeriasEnum.fromId(situacao);

        return valor === SituacaoParcelaFeriasEnum.HOMOLOGADA || valor === SituacaoParcelaFeriasEnum.EM_GOZO || valor === SituacaoParcelaFeriasEnum.USUFRUIDA;
    }

    situacaoParcela(indice: number): { descricao: string; codigoCor: string } {
        const situacao = this.parcelas[indice].situacao;
        const valor = SituacaoParcelaFeriasEnum.fromDesc(situacao) ?? SituacaoParcelaFeriasEnum.fromId(situacao);

        return {
            descricao: valor?.descricao || situacao || 'Nova parcela',
            codigoCor: valor?.extras?.codigoCor ?? SituacaoParcelaFeriasEnum.CANCELADA.extras.codigoCor
        };
    }

    private ehParcelaCancelada(parcela: ParcelaFerias): boolean {
        const situacao = parcela.situacao ?? '';
        return situacao === SituacaoParcelaFeriasEnum.CANCELADA.id || situacao === SituacaoParcelaFeriasEnum.CANCELADA.descricao;
    }

    private paraDataIso(valor: Date | string | undefined): string {
        if (!valor) {
            return '';
        }

        if (valor instanceof Date) {
            const ano = valor.getFullYear();
            const mes = `${valor.getMonth() + 1}`.padStart(2, '0');
            const dia = `${valor.getDate()}`.padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        }

        return String(valor).slice(0, 10);
    }

    private paraDataCalendario(valor: string): Date | null {
        return dataIsoValida(valor) ? getDateFromYYYYMMDD(valor) : null;
    }

    private ehPeriodoAquisitivo(valor: string | undefined): valor is PeriodoAquisitivo {
        return valor === 'ANC' || valor === 'PAQ';
    }

    dataPublicacaoChange(parcela: ParcelaAdministrativaComParcelas) {
        parcela.validacao.dataPublicacaoInvalida = false;
    }

    dataInterrupcaoChange(parcela: ParcelaAdministrativaComParcelas) {
        parcela.validacao.dataInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    dataMaximaInterrupcao(parcela: ParcelaAdministrativaComParcelas): Date | null {
        const dadosOriginais = this.dadosOriginaisInterrupcao.get(parcela);
        return dadosOriginais ? dadosOriginais.dataFim : parcela.dataFim;
    }

    private atualizarParcelaFilhaPorInterrupcao(parcela: ParcelaAdministrativaComParcelas): void {
        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        const dadosOriginais = this.dadosOriginaisInterrupcao.get(parcela);
        if (!parcelaFilha || !dadosOriginais) {
            return;
        }

        const dataInicio = this.paraDataCalendario(parcela.dataInicio);
        const dataInterrupcao = parcela.dataInterrupcao;
        const dataMinimaInterrupcao = parcela.dataMinimaInterrupcao;
        const dataMaximaInterrupcao = this.dataMaximaInterrupcao(parcela);
        const dataInterrupcaoIso = dataInterrupcao && !Number.isNaN(dataInterrupcao.getTime()) ? this.paraDataIso(dataInterrupcao) : null;
        const dataInterrupcaoInvalida = parcela.validacao.dataInterrupcaoInvalida || parcela.validacao.dataInterrupcaoForaPeriodoFerias || !dataInicio || !dataInterrupcao || Number.isNaN(dataInterrupcao.getTime()) || (dataMinimaInterrupcao !== null && dataInterrupcao < dataMinimaInterrupcao) || (dataMaximaInterrupcao !== null && dataInterrupcao > dataMaximaInterrupcao);
        const dataInterrupcaoAlterada = dataInterrupcaoIso !== dadosOriginais.dataInterrupcaoAplicada;

        if ((dataInterrupcaoAlterada || dataInterrupcaoInvalida) && parcelaFilha.selecionadaParaInterrupcao) {
            this.desistirSelecaoParcelaParaInterromper(parcelaFilha);
        }

        if (dataInterrupcaoInvalida || !dataInicio || !dataInterrupcao) {
            this.restaurarDadosOriginaisInterrupcao(parcela);
            dadosOriginais.dataInterrupcaoAplicada = null;
            return;
        }

        const diasAntesInterrupcao = differenceInDays(dataInterrupcao, dataInicio);
        const diasRestantes = dadosOriginais.quantidadeDias - diasAntesInterrupcao;
        if (diasAntesInterrupcao < 1 || diasRestantes < 1) {
            this.restaurarDadosOriginaisInterrupcao(parcela);
            dadosOriginais.dataInterrupcaoAplicada = null;
            return;
        }

        parcela.quantidadeDias = diasAntesInterrupcao;
        parcela.dataFim = new Date(dataInterrupcao.getFullYear(), dataInterrupcao.getMonth(), dataInterrupcao.getDate() - 1);
        parcela.situacao = SituacaoParcelaFeriasEnum.INTERROMPIDA.descricao;
        parcelaFilha.dataMinimaReprogramacao = this.calcularDataMinimaReprogramacao(dataInterrupcao);
        parcelaFilha.quantidadeDias = diasRestantes;
        this.atualizarDataFimParcela(this.parcelas.indexOf(parcelaFilha));
        dadosOriginais.dataInterrupcaoAplicada = dataInterrupcaoIso;
        this.registrarAlteracao();
    }

    blurDataPublicacaoChange(parcela: ParcelaAdministrativaComParcelas, event) {
        if (!parcela.dataPublicacaoDocumentoLegal && event.target.value !== '' && !event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            parcela.validacao.dataPublicacaoInvalida = true;
        }
    }

    blurDataInterrupcaoChange(parcela: ParcelaAdministrativaComParcelas, event) {
        if (!parcela.dataInterrupcao && event.target.value !== '' && !event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
            parcela.validacao.dataInterrupcaoInvalida = true;
        }
        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    inputDataPublicacaoChange(parcela: ParcelaAdministrativaComParcelas, event) {
        if (parcela.dataPublicacaoDocumentoLegal || event.target.value === '') {
            parcela.validacao.dataPublicacaoInvalida = false;
        } else if (event.target.value) {
            if (event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                //Verificar se o event.target.value que está no formado dd/mm/yyyy é uma data válida
                const partesData = event.target.value.split('/');
                if (isValidDate(parseInt(partesData[2]), parseInt(partesData[1]), parseInt(partesData[0]))) {
                    parcela.validacao.dataPublicacaoInvalida = false;
                } else {
                    parcela.validacao.dataPublicacaoInvalida = true;
                }
            }
        }
    }

    inputDataInterrupcaoChange(parcela: ParcelaAdministrativaComParcelas, event) {
        if (parcela.dataInterrupcao || event.target.value === '') {
            parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
            parcela.validacao.dataInterrupcaoInvalida = false;
        } else if (event.target.value) {
            if (event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                //Verificar se o event.target.value que está no formado dd/mm/yyyy é uma data válida
                const partesData = event.target.value.split('/');
                if (isValidDate(parseInt(partesData[2]), parseInt(partesData[1]), parseInt(partesData[0]))) {
                    parcela.validacao.dataInterrupcaoForaPeriodoFerias = true;
                    parcela.validacao.dataInterrupcaoInvalida = false;
                } else {
                    parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
                    parcela.validacao.dataInterrupcaoInvalida = true;
                }
            }
        }
        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    getMsgDataInterrupcaoForaPeriodoFerias(parcela: ParcelaAdministrativaComParcelas): string {
        return formatarMensagem(Message.ERRO_DATA_INICIO_PARCELA_FORA_DO_INTERVALO, {
            dataInicio: this.dataFormatadaPipe.transform(parcela.dataMinimaInterrupcao),
            dataFim: this.dataFormatadaPipe.transform(parcela.dataFim)
        });
    }

    onJustificativaChangeInterrupcao(event, parcela: ParcelaAdministrativaComParcelas) {
        parcela.justificativaInterrupcao = event.target.value ? event.target.value.trim() : '';
    }

    validarJustificativaInterrupcao(parcela: ParcelaAdministrativaComParcelas) {
        //if (!parcela.justificativaInterrupcao || parcela.justificativaInterrupcao.trim().length < 10 || parcela.justificativaInterrupcao.trim().length > 300) {
        if (parcela.justificativaInterrupcao && (parcela.justificativaInterrupcao.trim().length < 10 || parcela.justificativaInterrupcao.trim().length > 300)) {
            parcela.validacao.justificativaInterrupcaoInvalida = true;
        } else {
            parcela.validacao.justificativaInterrupcaoInvalida = false;
        }
    }
}
